import { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MultiSelectOption } from '@hqo/multi-select';
import { useIntl } from 'react-intl';

import { setCompanyFilter } from 'store/visits/actions';
import { getCompanyFilter, getCompanyOptions } from 'store/visits/selectors';
import { useCompaniesState } from 'store/companies/hooks';

export interface UseCompanyFilterReturn {
  options: MultiSelectOption[];
  selectedOptions: MultiSelectOption[];
  companyLabel: string;
  selectedCount: number;
  handleChange: (selected: MultiSelectOption[]) => void;
  isSet: boolean;
  isLoading: boolean;
}

export const useCompanyFilter = (): UseCompanyFilterReturn => {
  const dispatch = useDispatch();
  const value = useSelector(getCompanyFilter);
  const storedOptions = useSelector(getCompanyOptions);
  const { formatMessage } = useIntl();
  const companyLabel = formatMessage({ id: 'labels.company', defaultMessage: 'Company' });
  const { data: companiesData, loading } = useCompaniesState();

  const options: MultiSelectOption[] = useMemo((): MultiSelectOption[] => {
    if (!companiesData) {
      return [];
    }

    return companiesData.map((company) => ({
      value: company.uuid,
      label: company.name,
    }));
  }, [companiesData]);

  const handleChange = useCallback(
    (selected: MultiSelectOption[]) => {
      dispatch(setCompanyFilter(selected));
    },
    [dispatch],
  );

  // Derive selectedOptions from the persisted companyOptions so the chosen company
  // remains visible even when it leaves the date-scoped `options` (VIS-766) and
  // after page reload with pinned filters (VIS-767).
  const selectedOptions = useMemo(() => {
    const fromOptions = options.filter((option) => value.includes(option.value as string));
    const seen = new Set(fromOptions.map((option) => option.value as string));
    const fromStore = storedOptions.filter(
      (option) => value.includes(option.value as string) && !seen.has(option.value as string),
    );
    return [...fromOptions, ...fromStore];
  }, [options, storedOptions, value]);

  const selectedCount = useMemo(() => value.length, [value.length]);

  return {
    options,
    selectedOptions,
    companyLabel,
    selectedCount,
    handleChange,
    isSet: selectedOptions.length > 0,
    isLoading: loading,
  };
};
